import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatNumber, formatUsd, messages, type OrgSettings, type Quota } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/common/page';
import { cn } from '@/lib/utils';

/** S75 利用上限（admin のみ）。各項目に現在値と使用率を併記する */
export const SettingsLimitsPage = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Record<string, number>>({});

  const { data, isLoading } = useQuery({
    queryKey: qk.settings,
    queryFn: () =>
      api.get<SingleResponse<{ settings: OrgSettings; quota: Quota; organization: { name: string } }>>(
        '/settings',
      ),
  });

  useEffect(() => {
    if (!data) return;
    const s = data.data.settings;
    setForm({
      aiCostPerMonth: s.limits.aiCostPerMonth,
      searchPerMonth: s.limits.searchPerMonth,
      sendPerDay: s.limits.sendPerDay,
      crawlPerDay: s.limits.crawlPerDay,
      collectPerRun: s.limits.collectPerRun,
      warningThresholdPercent: s.limits.warningThresholdPercent,
      crawlDelaySeconds: s.crawl.delaySeconds,
      crawlConcurrency: s.crawl.concurrency,
      maxPagesPerCompany: s.crawl.maxPagesPerCompany,
      recrawlIntervalDays: s.crawl.recrawlIntervalDays,
      observationDays: s.metrics.observationDays,
      mailPerHour: s.send.mailPerHour,
      domainIntervalHours: s.send.domainIntervalHours,
      crawlResultsMonths: s.retention.crawlResultsMonths,
      screenshotsMonths: s.retention.screenshotsMonths,
      inboundBodyMonths: s.retention.inboundBodyMonths,
      auditLogsMonths: s.retention.auditLogsMonths,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/settings', {
        limits: {
          aiCostPerMonth: form.aiCostPerMonth,
          searchPerMonth: form.searchPerMonth,
          sendPerDay: form.sendPerDay,
          crawlPerDay: form.crawlPerDay,
          collectPerRun: form.collectPerRun,
          warningThresholdPercent: form.warningThresholdPercent,
        },
        crawl: {
          delaySeconds: form.crawlDelaySeconds,
          concurrency: form.crawlConcurrency,
          maxPagesPerCompany: form.maxPagesPerCompany,
          recrawlIntervalDays: form.recrawlIntervalDays,
        },
        metrics: { observationDays: form.observationDays },
        send: { mailPerHour: form.mailPerHour, domainIntervalHours: form.domainIntervalHours },
        retention: {
          crawlResultsMonths: form.crawlResultsMonths,
          screenshotsMonths: form.screenshotsMonths,
          inboundBodyMonths: form.inboundBodyMonths,
          auditLogsMonths: form.auditLogsMonths,
        },
      }),
    onSuccess: () => {
      toast.success(messages.common.saved);
      void queryClient.invalidateQueries({ queryKey: qk.settings });
      void queryClient.invalidateQueries({ queryKey: qk.quota });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (isLoading || !data) return <SkeletonRows rows={6} />;

  const quota = data.data.quota;

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.settings.limits}
        actions={
          <Button size="lg" onClick={() => save.mutate()} disabled={save.isPending}>
            {messages.common.save}
          </Button>
        }
      />

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>利用上限</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <LimitField
              label="月間 AI 費用（USD）"
              value={form.aiCostPerMonth}
              onChange={(v) => setForm((p) => ({ ...p, aiCostPerMonth: v }))}
              used={quota.aiCost.usedUsd}
              limit={quota.aiCost.limitUsd}
              level={quota.aiCost.level}
              format={(v) => formatUsd(v)}
            />
            <LimitField
              label="月間 検索呼び出し数"
              value={form.searchPerMonth}
              onChange={(v) => setForm((p) => ({ ...p, searchPerMonth: v }))}
              used={quota.search.usedCalls}
              limit={quota.search.limitCalls}
              level={quota.search.level}
              format={formatNumber}
            />
            <LimitField
              label="1 日あたり送信件数"
              value={form.sendPerDay}
              onChange={(v) => setForm((p) => ({ ...p, sendPerDay: v }))}
              used={quota.send.usedToday}
              limit={quota.send.dailyLimit}
              level={quota.send.level}
              format={formatNumber}
            />
            <LimitField
              label="1 日あたりクロール件数"
              value={form.crawlPerDay}
              onChange={(v) => setForm((p) => ({ ...p, crawlPerDay: v }))}
              used={quota.crawl.usedToday}
              limit={quota.crawl.dailyLimit}
              level={quota.crawl.level}
              format={formatNumber}
            />
            <NumberField
              label="1 回の収集件数上限"
              value={form.collectPerRun}
              onChange={(v) => setForm((p) => ({ ...p, collectPerRun: v }))}
            />
            <NumberField
              label="警告閾値（%）"
              value={form.warningThresholdPercent}
              onChange={(v) => setForm((p) => ({ ...p, warningThresholdPercent: v }))}
              hint="この割合を超えると警告を表示します"
            />
            <p className="text-note text-foreground-muted">
              {messages.usage.blockedFeatures}：AI 文面生成・AI 提案（上限到達で停止）、
              収集・送信（上限到達で新規受付を停止。実行中の処理は完了します）
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>クロール</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <NumberField
              label="同一ドメインへの間隔（秒）"
              value={form.crawlDelaySeconds}
              onChange={(v) => setForm((p) => ({ ...p, crawlDelaySeconds: v }))}
            />
            <NumberField
              label="全体の同時実行数"
              value={form.crawlConcurrency}
              onChange={(v) => setForm((p) => ({ ...p, crawlConcurrency: v }))}
            />
            <NumberField
              label="1 企業あたりの取得ページ数"
              value={form.maxPagesPerCompany}
              onChange={(v) => setForm((p) => ({ ...p, maxPagesPerCompany: v }))}
            />
            <NumberField
              label="再クロール間隔（日）"
              value={form.recrawlIntervalDays}
              onChange={(v) => setForm((p) => ({ ...p, recrawlIntervalDays: v }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>送信・集計</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <NumberField
              label="メール送信レート（毎時）"
              value={form.mailPerHour}
              onChange={(v) => setForm((p) => ({ ...p, mailPerHour: v }))}
            />
            <NumberField
              label="同一ドメイン間隔の既定（時間）"
              value={form.domainIntervalHours}
              onChange={(v) => setForm((p) => ({ ...p, domainIntervalHours: v }))}
            />
            <NumberField
              label="観測期間の既定（日）"
              value={form.observationDays}
              onChange={(v) => setForm((p) => ({ ...p, observationDays: v }))}
              hint="送信日から何日以内の返信・アポイントを成果として数えるか"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>保持期間（月）</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <NumberField
              label="クロール結果"
              value={form.crawlResultsMonths}
              onChange={(v) => setForm((p) => ({ ...p, crawlResultsMonths: v }))}
            />
            <NumberField
              label="送信スクリーンショット"
              value={form.screenshotsMonths}
              onChange={(v) => setForm((p) => ({ ...p, screenshotsMonths: v }))}
            />
            <NumberField
              label="受信メール本文"
              value={form.inboundBodyMonths}
              onChange={(v) => setForm((p) => ({ ...p, inboundBodyMonths: v }))}
            />
            <NumberField
              label="操作ログ"
              value={form.auditLogsMonths}
              onChange={(v) => setForm((p) => ({ ...p, auditLogsMonths: v }))}
            />
          </CardContent>
        </Card>
      </div>
    </>
  );
};

const NumberField = ({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: number | undefined;
  onChange: (value: number) => void;
  hint?: string;
}) => (
  <Field label={label} hint={hint}>
    {(props) => (
      <Input
        {...props}
        type="number"
        inputMode="numeric"
        min={0}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    )}
  </Field>
);

const LimitField = ({
  label,
  value,
  onChange,
  used,
  limit,
  level,
  format,
}: {
  label: string;
  value: number | undefined;
  onChange: (value: number) => void;
  used: number;
  limit: number | null;
  level: string;
  format: (value: number) => string;
}) => {
  const percent = limit && limit > 0 ? Math.min(Math.round((used / limit) * 100), 100) : 0;
  return (
    <div className="space-y-1.5">
      <NumberField label={label} value={value} onChange={onChange} />
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'h-2 flex-1 overflow-hidden rounded-control bg-muted',
            level !== 'ok' && 'ring-2 ring-foreground',
          )}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} の使用率`}
        >
          <div className="h-full bg-foreground" style={{ width: `${percent}%` }} />
        </div>
        <span
          className={cn(
            'tabular shrink-0 text-note text-foreground-muted',
            level !== 'ok' && 'font-semibold text-foreground',
          )}
        >
          {format(used)} / {limit ? format(limit) : '—'}（{percent}%）
        </span>
      </div>
    </div>
  );
};
